'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

/**
 * ListingLocationMap — 21:9 aspect map with the listing pin + optional
 * subdivision boundary overlay + lifestyle-line ("X min to Mt. Bachelor
 * · Y min downtown · Z min to Deschutes River trail").
 *
 * Spec source:
 *   design_system/ryan-realty/ui_kits/listing-detail/index.html §location
 *   docs/EXECUTION_PLAN.md §8 "What we BEAT" rank 6 — Bend lifestyle data
 *
 * Server-safe wrapper. The actual Google Maps client lives in
 * ListingLocationMap.client.tsx and loads via next/dynamic so the
 * @react-google-maps/api bundle only ships on listing-detail.
 *
 * Lifestyle line is caller-supplied (the page composes it from the
 * subdivisionName via a static lookup table OR via getCommunityMetadata
 * upstream). Renders nothing when no lat/lng is available.
 */

const ListingLocationMapClient = dynamic(() => import('./ListingLocationMap.client'), {
  ssr: false,
  loading: () => <MapPlaceholder />,
})

function MapPlaceholder() {
  return (
    <div
      aria-hidden
      className="h-full w-full"
      style={{
        background: 'var(--cream)',
        border: '1px solid color-mix(in srgb, var(--v3-navy) 18%, transparent)',
      }}
    />
  )
}

/**
 * True once the frame is within a screen of the viewport. The map sits well
 * below the fold; mounting it at hydration fetched the Google Maps script
 * while the hero photo was still the LCP candidate (P09).
 */
function useNearViewport() {
  const ref = useRef<HTMLDivElement>(null)
  const [near, setNear] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || near) return
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          io.disconnect()
        }
      },
      { rootMargin: '100% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [near])
  return { ref, near }
}

type Props = {
  lat: number | null
  lng: number | null
  /** Optional subdivision / neighborhood polygon overlay. */
  boundary?: GeoJSON.Geometry | null
  /** Free-form lifestyle bullets, rendered with middle-dot separators. */
  lifestyleLine?: string | null
  zoom?: number
  className?: string
  photoUrl?: string | null
  price?: number | null
  beds?: number | null
  baths?: number | null
  sqft?: number | null
  cityLine?: string | null
  href?: string | null
  inventoryHref?: string | null
  inventoryLabel?: string | null
}

export function ListingLocationMap({
  lat,
  lng,
  boundary,
  lifestyleLine,
  zoom,
  className,
  addressLine,
  photoUrl,
  price,
  beds,
  baths,
  sqft,
  cityLine,
  href,
  inventoryHref,
  inventoryLabel,
}: Props & { addressLine?: string | null }) {
  const { ref: frameRef, near } = useNearViewport()
  if (lat == null || lng == null) return null

  return (
    <>
    <section id="location" className={cn('section listing-map', className)}>
      <div className="sec-head">
        <div>
          <div className="eyebrow sec-index">Location</div>
          <h2 className="sec-title">Around this home</h2>
        </div>
      </div>

      <div
        className="relative w-full self-stretch"
        style={{
          aspectRatio: '16 / 9',
          minHeight: 'var(--v3-map-min)',
          marginTop: 'var(--v3-space-sm)',
        }}
      >
        <div ref={frameRef} className="absolute inset-0" style={{ minHeight: 'var(--v3-map-min)' }}>
          {near ? (
            <ListingLocationMapClient
              lat={lat}
              lng={lng}
              boundary={boundary ?? null}
              zoom={zoom}
              popup={
                addressLine
                  ? {
                      price: price ?? null,
                      photoURL: photoUrl ?? null,
                      streetLine: addressLine,
                      cityLine: cityLine?.trim() || '',
                      beds: beds ?? null,
                      baths: baths ?? null,
                      sqft: sqft ?? null,
                      href: href?.trim() || '#location',
                    }
                  : null
              }
            />
          ) : (
            <MapPlaceholder />
          )}
        </div>
      </div>
      {inventoryHref && inventoryLabel ? (
        <p style={{ marginTop: 'var(--v3-space-sm)' }}>
          <Link href={inventoryHref} className="listing-map__inventory">
            {`See everything for sale in ${inventoryLabel}`}
          </Link>
        </p>
      ) : null}
      {lifestyleLine ? (
        <div
          className="tabular-nums"
          style={{
            marginTop: 14,
            fontSize: '0.78rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
            color: 'color-mix(in srgb, var(--v3-navy) 72%, transparent)',
          }}
        >
          {lifestyleLine}
        </div>
      ) : null}
    </section>
    </>
  )
}
